import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGetSetsQuery } from 'store/services/sets/setsApi';
import type { SetInfo } from 'store/services/sets/setsApi';
import './SetMultiSelect.css';

interface SetMultiSelectProps {
  selectedSets: SetInfo[];
  onChange: (sets: SetInfo[]) => void;
  maxSelections?: number;
  disabled?: boolean;
  label?: string;
  hideCounter?: boolean;
  placeholder?: string;
}

const SetMultiSelect: React.FC<SetMultiSelectProps> = ({
  selectedSets,
  onChange,
  maxSelections = 5,
  disabled = false,
  label = 'Sets to Add',
  hideCounter = false,
  placeholder = 'Add a set...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch sets from API
  const { data: setsData, isLoading, error } = useGetSetsQuery({
    search: searchTerm || undefined,
    limit: 50,
    offset: 0
  }, { skip: !isOpen });

  // Debug: Log any errors
  if (error) {
    console.error('Error fetching sets:', error);
  }

  // Filter out already selected sets
  const availableSets = useMemo(() => {
    const sets = setsData?.sets || [];
    const selectedCodes = new Set(selectedSets.map(s => s.code));
    return sets.filter(set => !selectedCodes.has(set.code));
  }, [setsData?.sets, selectedSets]);

  const isMaxReached = selectedSets.length >= maxSelections;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard events
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleSelect = (set: SetInfo) => {
    if (isMaxReached) return;
    onChange([...selectedSets, set]);
    setSearchTerm('');
  };

  const handleRemove = (setCode: string) => {
    onChange(selectedSets.filter(s => s.code !== setCode));
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (isOpen) {
      setSearchTerm('');
    }
  };

  return (
    <div className="set-multi-select" ref={dropdownRef} onKeyDown={handleKeyDown}>
      <label>{label}</label>

      {/* Selected Sets Chips */}
      {selectedSets.length > 0 && (
        <div className="set-multi-select-chips">
          {selectedSets.map(set => (
            <div key={set.code} className="set-multi-select-chip">
              <span className="set-chip-name">
                {set.name} ({set.code.toUpperCase()})
              </span>
              <span className="set-chip-count">{set.cardCount} cards</span>
              <button
                type="button"
                className="set-chip-remove"
                onClick={() => handleRemove(set.code)}
                disabled={disabled}
                aria-label={`Remove ${set.name}`}
              >
                <FontAwesomeIcon icon="times" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropdown Trigger */}
      <button
        type="button"
        className={`set-multi-select-trigger ${isOpen ? 'open' : ''} ${isMaxReached ? 'max-reached' : ''}`}
        onClick={toggleDropdown}
        disabled={disabled || isMaxReached}
      >
        <FontAwesomeIcon icon="plus" className="set-multi-select-plus" />
        <span className="set-multi-select-trigger-text">
          {isMaxReached ? `Maximum ${maxSelections} sets` : placeholder}
        </span>
        <svg
          className={`set-multi-select-chevron ${isOpen ? 'rotated' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Selection Counter */}
      {!hideCounter && (
        <div className="set-multi-select-counter">
          {selectedSets.length} of {maxSelections} sets selected
        </div>
      )}

      {/* Dropdown Panel */}
      {isOpen && !isMaxReached && (
        <div className="set-multi-select-dropdown">
          <div className="set-multi-select-search-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              className="set-multi-select-search"
              placeholder="Search sets by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="set-multi-select-options">
            {isLoading ? (
              <div className="set-multi-select-loading">Loading sets...</div>
            ) : error ? (
              <div className="set-multi-select-error">
                Failed to load sets. Please try again.
              </div>
            ) : availableSets.length > 0 ? (
              availableSets.map(set => (
                <button
                  key={set.code}
                  type="button"
                  className="set-multi-select-option"
                  onClick={() => handleSelect(set)}
                >
                  <span className="set-option-name">{set.name}</span>
                  <span className="set-option-meta">
                    ({set.code.toUpperCase()}) - {set.cardCount} cards
                  </span>
                </button>
              ))
            ) : (
              <div className="set-multi-select-no-results">
                {searchTerm ? 'No sets found' : 'No sets available'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SetMultiSelect;
